import { call, put, takeLatest } from "redux-saga/effects";
import { routerActions, replace } from "connected-react-router";

import moment from "moment";

import { types, actions as investmentfundsActions } from "reducers/investmentfunds";
import * as investmentfunds from "middleware/investmentfunds";
import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import * as config from "util/config";
import { downloadPdf } from "util/download";

const sagas = [
    takeLatest(types.INVESTOR_TEST_PRE_REQUEST, investorTestPre),
    takeLatest(types.INVESTOR_TEST_SEND_REQUEST, investorTestSend),
    takeLatest(types.INVESTOR_PROFILE_READ_REQUEST, investorProfileRead),
    takeLatest(types.DASHBOARD_PRE_REQUEST, dashboardPre),
    takeLatest(types.ACCOUNT_OFFICER_CONTACT_REQUEST, accountOfficerContact),
    takeLatest(types.ACCOUNTS_LIST_REQUEST, listAccounts),
    takeLatest(types.ACCOUNTS_ADD_PRE_REQUEST, addAccountPre),
    takeLatest(types.ACCOUNTS_ADD_SEND_REQUEST, addAccountSend),
    takeLatest(types.DOWNLOAD_ACCOUNT_TERMS_REQUEST, downloadAccountTerms),
    takeLatest(types.ACCOUNTS_ADD_UPLOAD_DOC_REQUEST, accountDocumentUpload),
    takeLatest(types.ACCOUNTS_ADD_DELETE_DOC_REQUEST, accountDocumentDelete),
];

export default sagas;

function* investorTestPre() {
    const response = yield call(investmentfunds.investorTestPre);

    if (response.type === "W") {
        yield put({
            type: types.INVESTOR_TEST_PRE_FAILURE,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { questions } = response.data.data;

        yield put({
            type: types.INVESTOR_TEST_PRE_SUCCESS,
            questions,
        });
    }
}

function* investorTestSend({ params }) {
    const response = yield call(investmentfunds.investorTestSend, params);
    if (response.type === "W") {
        yield put({
            type: types.INVESTOR_TEST_SEND_FAILURE,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { investorProfile } = response.data.data;

        yield put({
            type: types.INVESTOR_TEST_SEND_SUCCESS,
            investorProfile,
            testUpdateMessage: "investmentFunds.investorTest.update",
        });
        yield put(routerActions.push("/profileInfo"));
    }
}

function* investorProfileRead() {
    const response = yield call(investmentfunds.investorProfileRead);

    if (response.type === "W") {
        yield put({
            type: types.INVESTOR_PROFILE_READ_FAILURE,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { investorProfile, lastTestDate } = response.data.data;
        let expired = false;
        const defaultTZ = config.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");

        if (investorProfile) {
            if (
                moment(lastTestDate, "DD-MM-yyyy")
                    .add(1, "year")
                    .tz(defaultTZ)
                    .endOf("day")
                    .isBefore(
                        moment(new Date(), "DD-MM-yyyy")
                            .tz(defaultTZ)
                            .endOf("day"),
                    )
            ) {
                expired = true;
                yield put(
                    notificationActions.showNotification(
                        i18n.get("investmentFunds.investorTest.expired.message"),
                        "warning",
                        ["investmentfunds"],
                    ),
                );
            }
        } else {
            yield put(routerActions.push("/noProfileInfo"));
        }

        yield put({
            type: types.INVESTOR_PROFILE_READ_SUCCESS,
            investorProfile,
            lastTestDate,
            expired,
        });
    }
}

function* dashboardPre() {
    const response = yield call(investmentfunds.dashboardPre);

    if (response.type === "W") {
        yield put({
            type: types.DASHBOARD_PRE_FAILURE,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { accountsOfficer, deposits, fci, bac } = response.data.data;

        yield put({
            type: types.DASHBOARD_PRE_SUCCESS,
            accountsOfficer,
            deposits,
            fci,
            bac,
        });
    }
}

function* accountOfficerContact({ params }) {
    const response = yield call(investmentfunds.accountOfficerContact, params);
    const { subject, from, to, body } = response.data.data;

    if (response.type === "W") {
        yield put({ type: types.ACCOUNT_OFFICER_CONTACT_FAILURE });

        params.formikBag.setErrors({
            subject,
            from,
            to,
            body,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        yield put({ type: types.ACCOUNT_OFFICER_CONTACT_SUCCESS });
    }
}

function* listAccounts() {
    const response = yield call(investmentfunds.listAccounts);
    const { accountsInfo } = response.data.data;

    if (response.type === "W") {
        yield put({ type: types.ACCOUNTS_LIST_FAILURE });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        yield put({ type: types.ACCOUNTS_LIST_SUCCESS, accountsInfo });
    }
}

function* addAccountPre() {
    const response = yield call(investmentfunds.investorProfileRead);

    if (response.type === "W") {
        yield put({
            type: types.ACCOUNTS_ADD_PRE_FAILURE,
        });
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { investorProfile, lastTestDate } = response.data.data;
        let expired = false;
        const defaultTZ = config.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");

        if (
            !investorProfile ||
            moment(lastTestDate, "DD-MM-yyyy")
                .add(1, "year")
                .tz(defaultTZ)
                .endOf("day")
                .isBefore(
                    moment(new Date(), "DD-MM-yyyy")
                        .tz(defaultTZ)
                        .endOf("day"),
                )
        ) {
            expired = true;
        }

        yield put({
            type: types.ACCOUNTS_ADD_PRE_SUCCESS,
            expired,
        });
        if (!investorProfile) {
            yield put(investmentfundsActions.firstInvestorTest());
        }
    }
}

function* addAccountSend({ params }) {
    const response = yield call(investmentfunds.addAccountSend, params);
    const { fciAccount, bonusesAccount } = response.data.data;

    if (response.type === "W") {
        let message;
        if (response.data?.data?.otp) {
            message = i18n.get("login.step1.secondFactor.invalid");

            params.formikBag.setErrors({
                credential: message,
            });
        } else {
            message = i18n.get("global.unexpectedError");
        }

        yield put(notificationActions.showNotification(message, "error", ["investmentfunds"]));
        yield put({ type: types.ACCOUNTS_ADD_SEND_FAILURE });
    } else {
        yield put({
            type: types.ACCOUNTS_ADD_SEND_SUCCESS,
            fciAccount,
            bonusesAccount,
        });
        yield put(replace(`/investmentFunds/accounts/add/transaction/${response.data.idTransaction}`));
    }
}

function* downloadAccountTerms() {
    const { type, data } = yield call(investmentfunds.downloadAccountTerms);

    if (type === "W") {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
    } else {
        const { content, fileName } = data.data;
        downloadPdf(fileName, content);
    }
}

function* accountDocumentUpload({ documentToUpload }) {
    const response = yield call(investmentfunds.accountDocumentUpload, documentToUpload);

    if (response && response.status === 200) {
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.data.errorMsg, "error", ["investmentfunds"]));
            yield put({
                type: types.ACCOUNTS_ADD_UPLOAD_DOC_FAILURE,
                documentToUploadRequest: response?.data?.data?.errorMsg,
                documentCode: documentToUpload.uploaderRequestId,
            });
        } else {
            yield put({
                type: types.ACCOUNTS_ADD_UPLOAD_DOC_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: documentToUpload.uploaderRequestId,
            });
        }
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
        yield put({ type: types.ACCOUNTS_ADD_UPLOAD_DOC_FAILURE });
    }
}

function* accountDocumentDelete({ documentId }) {
    const params = {
        documentId,
    };
    const response = yield call(investmentfunds.accountDocumentDelete, params);

    if (response && response.status === 200) {
        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.data.errorMsg, "error", ["investmentfunds"]));
            yield put({ type: types.ACCOUNTS_ADD_DELETE_DOC_FAILURE });
        } else {
            yield put({
                type: types.ACCOUNTS_ADD_DELETE_DOC_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: "1",
            });
        }
    } else {
        yield put(
            notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["investmentfunds"]),
        );
        yield put({ type: types.ACCOUNTS_ADD_DELETE_DOC_FAILURE });
    }
}
